/**
 * What a stylizer control acts on.
 *
 * A control in the toolbar works on *the author's own words*, and the author
 * shows which words they mean in one of two ways: by selecting them, or by
 * leaving the cursor somewhere and pressing the control. A selection is already
 * a range; a bare cursor is read as the word it sits in or in, or as the sentence
 * around it, which is the granularity of the two controls that need one:
 *
 *  - a **Kashida** stretches a single word, so it is drawn between the second to
 *    last and the last letter of the word under the cursor, and
 *  - the **smart highlight** paints a phrase, which is what a sentence is.
 *
 * Pure, and outside the toolbar rather than inside it, because of *when* the
 * question is asked: the composer's input keeps its selection in a ref and does
 * not re-render for it (`text-input/TextInput.tsx`), so the range is read at the
 * moment a control is pressed — and this is the answer to that read.
 */
import {
  trimToWords,
  sentenceAt,
  wordAt,
  type TextRange,
} from '#/state/postStyle/typography'

/** How much text a control takes when the author has not selected any. */
export type StylizerTarget = 'word' | 'sentence'

/**
 * The stretch of `text` a control should act on, or `null` when there is none.
 *
 * `null` is a real answer: a cursor in an empty post, or between two spaces with
 * nothing to stretch, means the control has been pressed with nothing to act on,
 * and the panel says so rather than writing a range over whitespace.
 */
export function stylizerRange(
  text: string,
  selection: TextRange | null | undefined,
  target: StylizerTarget,
): TextRange | null {
  if (!text) return null

  /*
   * A real selection is the whole answer: the author has already said which words
   * they mean, so nothing here second-guesses them. A selection that does not
   * meet this text at all — the post changed under it — is answered with `null`
   * rather than by quietly falling back to the cursor.
   */
  if (selection && selection.end > selection.start) {
    /*
     * A double-click and a double-tap both tend to take the space after the word,
     * and a selection dragged by hand often takes the one before it as well — so
     * every answer here is the *words*, never a range with whitespace on an end.
     * Which of the two kinds of control was asked does not change that: a highlight
     * that began or ended on the space would paint a coloured block beside the words
     * rather than behind them, and a Kashida asked of a word plus a space used to be
     * refused outright, which is the one answer an author cannot act on.
     */
    return trimToWords(text, {start: selection.start, end: selection.end})
  }

  const at = selection?.start ?? text.length
  const range = target === 'sentence' ? sentenceAt(text, at) : wordAt(text, at)
  if (!range || range.end <= range.start) return null
  return trimToWords(text, range)
}
